import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { getUserBusinessesService, IBusiness } from '../services/business';
import { useAuth } from '../hooks/use-auth';

interface BusinessSetupContextState {
  hasSetupAccount: boolean | null;
  loading: boolean;
  business: IBusiness | null;
  /** True when the lookup has failed and its automatic retries are exhausted,
   * so the app can offer a retry instead of treating an unreachable API as
   * "this user has no business". */
  lookupFailed: boolean;
  /** Clears the failure so the layout's effect will attempt the lookup again. */
  retryBusinessSetup(): void;
  checkBusinessSetup(token?: string): Promise<boolean>;
  setHasSetupAccount(value: boolean): void;
  setBusiness(business: IBusiness | null): void;
}

/** Transient failures are common; three tries is enough to ride one out. */
const MAX_LOOKUP_ATTEMPTS = 3;

const BusinessSetupContext = createContext<
  BusinessSetupContextState | undefined
>(undefined);

export const useBusinessSetup = (): BusinessSetupContextState => {
  const ctx = useContext(BusinessSetupContext);
  if (!ctx) {
    throw new Error(
      'useBusinessSetup must be used within BusinessSetupProvider'
    );
  }
  return ctx;
};

export const BusinessSetupProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const { accessToken } = useAuth();
  const [hasSetupAccount, setHasSetupAccount] = useState<boolean | null>(null);
  const [business, setBusiness] = useState<IBusiness | null>(null);
  const [loading, setLoading] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);
  // Bounded so a persistently unreachable API cannot spin the layout effect.
  const attemptsRef = useRef(0);

  const checkBusinessSetup = useCallback(
    async (token?: string): Promise<boolean> => {
      setLoading(true);
      try {
        const tokenToUse = token || accessToken || undefined;

        const response = await getUserBusinessesService(tokenToUse);

        const businesses = response?.success ? response.data.businesses : [];
        const hasBusinesses = Boolean(businesses && businesses.length > 0);

        if (hasBusinesses) {
          setBusiness(businesses[0]);
        } else {
          setBusiness(null);
        }

        attemptsRef.current = 0;
        setLookupFailed(false);
        setHasSetupAccount(hasBusinesses);
        return hasBusinesses;
      } catch (error) {
        // A failed request means we do not know, not that the user has no
        // business. Setting `hasSetupAccount` false here used to redirect an
        // established customer into the onboarding wizard, with no way back,
        // any time the API hiccuped.
        console.error('Error checking business setup:', error);
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_LOOKUP_ATTEMPTS) {
          setLookupFailed(true);
        }
        return false;
      } finally {
        setLoading(false);
      }
    },
    [accessToken]
  );

  const retryBusinessSetup = useCallback(() => {
    attemptsRef.current = 0;
    setLookupFailed(false);
  }, []);

  const value = useMemo(
    () => ({
      hasSetupAccount,
      loading,
      business,
      lookupFailed,
      checkBusinessSetup,
      retryBusinessSetup,
      setHasSetupAccount,
      setBusiness,
    }),
    [
      hasSetupAccount,
      loading,
      business,
      lookupFailed,
      checkBusinessSetup,
      retryBusinessSetup,
    ]
  );

  return (
    <BusinessSetupContext.Provider value={value}>
      {children}
    </BusinessSetupContext.Provider>
  );
};
